import { getAuthToken } from "./authApi";
import { isSidebarQuote } from "./quoteStatus";

export const PRODUCTS_API_URL = "https://uktomars.co.uk/api/products";
export const CATEGORIES_API_URL = "https://uktomars.co.uk/api/categories";
export const BRANDS_API_URL = "https://uktomars.co.uk/api/brands";
export const EVENTS_API_URL = "https://uktomars.co.uk/api/events";
export const QUOTE_API_URL = "https://uktomars.co.uk/api/quotes";

function formatQuoteErrorMessage(message, status) {
  const text = String(message || "").trim();
  if (!text) {
    return status === 401
      ? "Your session has expired. Please sign in again."
      : "Unable to submit quote request. Please try again.";
  }

  const lower = text.toLowerCase();
  if (
    lower.includes("smtp") ||
    lower.includes("authenticator") ||
    lower.includes("535 5.7.8") ||
    lower.includes("mailer")
  ) {
    return (
      "Your quote could not be completed because the server email account " +
      "(sales@uktomars.co.uk) is not configured correctly. " +
      "Please contact sales@uktomars.co.uk with your basket details, " +
      "or ask your site administrator to update the API mail settings."
    );
  }

  return text;
}

export async function submitQuote({ note = "", lines }) {
  const token = getAuthToken();
  if (!token) {
    const error = new Error(
      "Please sign in to your trade account to request a quote.",
    );
    error.code = "AUTH_REQUIRED";
    throw error;
  }

  const response = await fetch(QUOTE_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ note, lines }),
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const rawMessage = payload?.message || payload?.error || "";
    const message = formatQuoteErrorMessage(rawMessage, response.status);
    const error = new Error(message);
    error.status = response.status;
    error.rawMessage = rawMessage;
    if (response.status === 401) {
      error.code = "AUTH_REQUIRED";
    }
    throw error;
  }

  return payload;
}

export async function fetchQuotes({ page = 1 } = {}) {
  const token = getAuthToken();
  if (!token) {
    const error = new Error("Please sign in to view your quote history.");
    error.code = "AUTH_REQUIRED";
    throw error;
  }

  const url =
    page > 1
      ? `${QUOTE_API_URL}?page=${encodeURIComponent(page)}`
      : QUOTE_API_URL;

  const response = await fetch(url, {
    cache: "no-store",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      payload?.message ||
      payload?.error ||
      (response.status === 401
        ? "Your session has expired. Please sign in again."
        : "Unable to load quote history. Please try again.");
    const error = new Error(message);
    error.status = response.status;
    if (response.status === 401) {
      error.code = "AUTH_REQUIRED";
    }
    throw error;
  }

  return payload;
}

export async function fetchAllUserQuotes() {
  const all = [];
  let page = 1;
  let lastPage = 1;

  while (page <= lastPage) {
    const payload = await fetchQuotes({ page });
    const data = Array.isArray(payload?.data) ? payload.data : [];
    all.push(...data);
    lastPage = payload?.meta?.last_page ?? 1;
    page += 1;
  }

  return all.map(normalizeQuoteLines);
}

export async function fetchQuote(quoteId) {
  const token = getAuthToken();
  if (!token) {
    const error = new Error("Please sign in to view this quote.");
    error.code = "AUTH_REQUIRED";
    throw error;
  }

  const id = String(quoteId ?? "").trim();
  if (!id) {
    throw new Error("Quote not found.");
  }

  const response = await fetch(`${QUOTE_API_URL}/${encodeURIComponent(id)}`, {
    cache: "no-store",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      payload?.message ||
      payload?.error ||
      (response.status === 404
        ? "Quote not found."
        : response.status === 401
          ? "Your session has expired. Please sign in again."
          : "Unable to load quote details. Please try again.");
    const error = new Error(message);
    error.status = response.status;
    if (response.status === 401) {
      error.code = "AUTH_REQUIRED";
    }
    throw error;
  }

  return normalizeQuoteLines(payload?.data ?? payload);
}

export function getActiveQuoteLines(lines) {
  return (Array.isArray(lines) ? lines : []).filter(
    (line) => (Number(line?.quantity) || 0) > 0,
  );
}

export function normalizeQuoteLines(quote) {
  if (!quote) return quote;
  return {
    ...quote,
    lines: getActiveQuoteLines(quote.lines),
  };
}

function quoteAuthHeaders(token) {
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
}

function formatQuoteUpdateError(payload, response) {
  const rawMessage = payload?.message || payload?.error || "";
  if (response.status === 405 || response.status === 404) {
    return (
      rawMessage ||
      "Quote editing is not available on the server yet. Please ask your administrator to enable quote line update routes."
    );
  }
  if (response.status === 403) {
    return rawMessage || "This quote can no longer be edited.";
  }
  if (response.status === 401) {
    return rawMessage || "Your session has expired. Please sign in again.";
  }
  return (
    formatQuoteErrorMessage(rawMessage, response.status) ||
    "Unable to update quote line. Please try again."
  );
}

function normalizeQuoteLineId(lineId) {
  const id = Number(lineId);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error("Quote line not found.");
  }
  return id;
}

function buildQuoteLinesPayload(lines, { allowZeroQuantity = false } = {}) {
  const normalized = (Array.isArray(lines) ? lines : [])
    .map((line) => ({
      id: line?.id,
      quantity: Number(line?.quantity) || 0,
    }))
    .filter((line) => line.id != null);

  if (allowZeroQuantity) {
    return normalized;
  }

  return normalized.filter((line) => line.quantity > 0);
}

export async function updateQuote(
  quoteId,
  { lines, note, status, allowZeroQuantity = false } = {},
) {
  const token = getAuthToken();
  if (!token) {
    const error = new Error("Please sign in to update this quote.");
    error.code = "AUTH_REQUIRED";
    throw error;
  }

  const id = String(quoteId ?? "").trim();
  if (!id) {
    throw new Error("Quote not found.");
  }

  const body = {};
  if (lines != null) {
    body.lines = buildQuoteLinesPayload(lines, { allowZeroQuantity });
  }
  if (note !== undefined) {
    body.note = note;
  }
  if (status !== undefined && status !== null && String(status).trim() !== "") {
    body.status = String(status).toLowerCase().trim();
  }

  const response = await fetch(`${QUOTE_API_URL}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: quoteAuthHeaders(token),
    body: JSON.stringify(body),
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = formatQuoteUpdateError(payload, response);
    const error = new Error(message);
    error.status = response.status;
    if (response.status === 401) {
      error.code = "AUTH_REQUIRED";
    }
    throw error;
  }

  return normalizeQuoteLines(payload?.data ?? payload);
}

/** Update quote line quantities only — never sends status. */
export async function updateQuoteLines(
  quoteId,
  lines,
  { allowZeroQuantity = false } = {},
) {
  return updateQuote(quoteId, { lines, allowZeroQuantity });
}

export async function updateQuoteLineQuantity(quoteId, lineId, quantity) {
  const nextQuantity = Number(quantity);
  if (!Number.isFinite(nextQuantity) || nextQuantity < 1) {
    throw new Error("Quantity must be at least 1.");
  }

  return updateQuoteLines(quoteId, [{ id: lineId, quantity: nextQuantity }]);
}

/** Update one line — sends only that line in the PATCH body. */
export async function updateSingleQuoteLine(quote, lineId, nextQuantity) {
  return updateQuoteLineQuantity(quote.id, lineId, nextQuantity);
}

/**
 * PATCH /quotes/{id} — accept quote only.
 * Body: { "status": "accepted" }
 */
export async function acceptQuote(quoteId) {
  return updateQuote(quoteId, { status: "accepted" });
}

function isQuantityMinValidationError(error) {
  const message = String(error?.message || "").toLowerCase();
  return message.includes("at least 1") || message.includes("minimum");
}

export async function deleteQuoteLine(quoteId, lineId) {
  const normalizedLineId = normalizeQuoteLineId(lineId);

  try {
    return await updateQuote(quoteId, {
      lines: [{ id: normalizedLineId, quantity: 0 }],
      allowZeroQuantity: true,
    });
  } catch (error) {
    if (!isQuantityMinValidationError(error)) {
      throw error;
    }

    const quote = await fetchQuote(quoteId);
    const lines = getActiveQuoteLines(quote?.lines).filter(
      (line) => Number(line.id) !== normalizedLineId,
    );

    return updateQuote(quoteId, { lines });
  }
}

export async function fetchPendingQuotes() {
  const quotes = await fetchAllUserQuotes();
  return quotes.filter(isSidebarQuote);
}

const SHOP_CACHE_TTL_MS = 5 * 60 * 1000;
const SHOP_FETCH_MAX_ATTEMPTS = 6;
const PRODUCT_PAGE_SIZE = 24;
const PRODUCT_BULK_PAGE_SIZE = 100;

const shopResponseCache = new Map();
const shopInflight = new Map();
const shopListCache = new Map();

let shopRateLimitUntil = 0;

function sleep(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function retryDelayMs(attempt, retryAfterHeader) {
  const fromHeader = Number.parseInt(retryAfterHeader, 10);
  if (Number.isFinite(fromHeader) && fromHeader > 0) {
    return Math.min(fromHeader * 1000, 20000);
  }
  return Math.min(1500 * 2 ** attempt, 20000);
}

function isRetryableStatus(status) {
  return status === 429 || status === 503 || status === 502;
}

async function waitForShopRateLimit() {
  const waitMs = shopRateLimitUntil - Date.now();
  if (waitMs > 0) await sleep(waitMs);
}

function armShopRateLimit(retryAfterHeader, attempt = 0) {
  const delay = retryDelayMs(attempt, retryAfterHeader);
  shopRateLimitUntil = Math.max(shopRateLimitUntil, Date.now() + delay);
}

export async function fetchShopJson(url, { ttlMs = SHOP_CACHE_TTL_MS } = {}) {
  const key = String(url);
  const cached = shopResponseCache.get(key);
  if (cached && cached.expires > Date.now()) {
    return cached.data;
  }

  const existing = shopInflight.get(key);
  if (existing) return existing;

  const request = (async () => {
    let lastError;
    for (let attempt = 0; attempt < SHOP_FETCH_MAX_ATTEMPTS; attempt += 1) {
      try {
        await waitForShopRateLimit();
        const response = await fetch(key, {
          headers: { Accept: "application/json" },
        });

        if (isRetryableStatus(response.status)) {
          lastError = new Error(`Failed to fetch ${key}`);
          lastError.status = response.status;
          armShopRateLimit(response.headers.get("Retry-After"), attempt);
          await waitForShopRateLimit();
          continue;
        }

        if (!response.ok) {
          const error = new Error(`Failed to fetch ${key}`);
          error.status = response.status;
          throw error;
        }

        const data = await response.json();
        if (ttlMs > 0) {
          shopResponseCache.set(key, {
            expires: Date.now() + ttlMs,
            data,
          });
        }
        return data;
      } catch (error) {
        lastError = error;
        if (error?.status && !isRetryableStatus(error.status)) {
          throw error;
        }
        if (attempt < SHOP_FETCH_MAX_ATTEMPTS - 1) {
          await sleep(retryDelayMs(attempt));
        }
      }
    }

    throw lastError || new Error(`Failed to fetch ${key}`);
  })().finally(() => {
    shopInflight.delete(key);
  });

  shopInflight.set(key, request);
  return request;
}

function uniqueTrimmedStrings(values) {
  const list = Array.isArray(values)
    ? values
    : values != null && values !== ""
      ? [values]
      : [];
  const seen = new Set();
  const out = [];
  list.forEach((value) => {
    const text = String(value ?? "").trim();
    if (!text || seen.has(text)) return;
    seen.add(text);
    out.push(text);
  });
  return out;
}

function appendQueryList(params, { arrayKey, scalarKey, values }) {
  const list = uniqueTrimmedStrings(values);
  if (!list.length) return;
  if (list.length === 1 && scalarKey) {
    params.set(scalarKey, list[0]);
    return;
  }
  list.forEach((value) => params.append(arrayKey, value));
}

function hasProductFilterParams(searchParams) {
  return [
    "brand_id",
    "brand_ids[]",
    "brand_ids",
    "q",
    "category",
    "search",
    "filter",
    "filters[]",
    "filters",
    "filter_id",
    "filter_ids[]",
    "filter_ids",
  ].some((key) => Boolean(searchParams.get(key)));
}

function isUnfilteredProductsUrl(url) {
  try {
    const parsed = new URL(url, PRODUCTS_API_URL);
    const path = parsed.pathname.replace(/\/+$/, "");
    if (!path.endsWith("/products")) return false;
    return !hasProductFilterParams(parsed.searchParams);
  } catch {
    return false;
  }
}

async function collectAllPages(url) {
  const parsed = new URL(url, PRODUCTS_API_URL);
  if (!parsed.searchParams.has("per_page")) {
    parsed.searchParams.set("per_page", String(PRODUCT_BULK_PAGE_SIZE));
  }

  const all = [];
  let page = Number(parsed.searchParams.get("page") || 1) || 1;
  let lastPage = 1;

  do {
    parsed.searchParams.set("page", String(page));
    const payload = await fetchShopJson(parsed.toString());
    const data = Array.isArray(payload?.data) ? payload.data : [];
    all.push(...data);

    const metaLast = Number(payload?.meta?.last_page);
    if (!Number.isFinite(metaLast) || metaLast < 1) {
      break;
    }

    if (page === 1 && metaLast > 1 && isUnfilteredProductsUrl(url)) {
      throw new Error(
        "Refusing to download the full product catalog. Load products page-by-page.",
      );
    }

    lastPage = metaLast;
    page += 1;
  } while (page <= lastPage);

  return all;
}

export async function fetchAllPaginated(url) {
  const key = `all:${url}`;
  const cached = shopListCache.get(key);
  if (cached && cached.expires > Date.now()) {
    return cached.data;
  }

  const existing = shopInflight.get(key);
  if (existing) return existing;

  const request = collectAllPages(url)
    .then((data) => {
      shopListCache.set(key, {
        expires: Date.now() + SHOP_CACHE_TTL_MS,
        data,
      });
      return data;
    })
    .finally(() => {
      shopInflight.delete(key);
    });

  shopInflight.set(key, request);
  return request;
}

export async function fetchAllBrands() {
  const payload = await fetchShopJson(BRANDS_API_URL);
  return Array.isArray(payload?.data) ? payload.data : [];
}

export const getProductsByBrandUrl = (brandId) => {
  const id = String(brandId ?? "").trim();
  if (!id) return "";
  const params = new URLSearchParams({
    brand_id: id,
    per_page: String(PRODUCT_BULK_PAGE_SIZE),
  });
  return `${PRODUCTS_API_URL}?${params.toString()}`;
};

export const fetchProductsByBrand = async (brandId) => {
  const url = getProductsByBrandUrl(brandId);
  if (!url) return [];
  return fetchAllPaginated(url);
};

export const findBrandById = async (brandId) => {
  const id = String(brandId ?? "").trim();
  if (!id) return null;

  const brands = await fetchAllBrands();
  return brands.find((brand) => String(brand?.id) === id) ?? null;
};

export const getBrandProductsPath = (brand) => {
  if (brand?.id == null) return "/Brands";
  const id = encodeURIComponent(String(brand.id));
  return `/Brands/${id}/products?brand_id=${id}`;
};

export const getProductsListUrl = ({
  category = "",
  subcategory = "",
  q = "",
  brandId = "",
  brandIds = [],
  filters = [],
  page = 1,
  perPage = PRODUCT_PAGE_SIZE,
} = {}) => {
  const params = new URLSearchParams();
  if (subcategory) {
    params.set("category", subcategory);
  } else if (category) {
    params.set("category", category);
  }
  if (q) params.set("q", String(q).trim());
  const resolvedBrandIds = uniqueTrimmedStrings(
    brandIds.length ? brandIds : brandId,
  );
  appendQueryList(params, {
    arrayKey: "brand_ids[]",
    scalarKey: "brand_id",
    values: resolvedBrandIds,
  });
  appendQueryList(params, {
    arrayKey: "filters[]",
    scalarKey: "filter",
    values: filters,
  });
  params.set("page", String(page || 1));
  params.set("per_page", String(perPage || PRODUCT_PAGE_SIZE));
  return `${PRODUCTS_API_URL}?${params.toString()}`;
};

export async function fetchProductsPage({
  category = "",
  subcategory = "",
  q = "",
  brandId = "",
  brandIds = [],
  filters = [],
  page = 1,
  perPage = PRODUCT_PAGE_SIZE,
} = {}) {
  const url = getProductsListUrl({
    category,
    subcategory,
    q,
    brandId,
    brandIds,
    filters,
    page,
    perPage,
  });
  const payload = await fetchShopJson(url);
  const data = Array.isArray(payload?.data) ? payload.data : [];
  return {
    data,
    meta: payload?.meta ?? {},
  };
}

export function productMatchesSearchQuery(product, query) {
  const q = String(query ?? "")
    .trim()
    .toLowerCase();
  if (!q) return true;
  const compactQ = q.replace(/[\s-]/g, "");
  return [
    product?.name,
    product?.code,
    product?.sku,
    product?.unit_barcode,
    product?.case_barcode,
    product?.barcode,
  ].some((value) => {
    const text = String(value ?? "").toLowerCase();
    if (!text) return false;
    if (text.includes(q)) return true;
    const compact = text.replace(/[\s-]/g, "");
    return compactQ.length >= 4 && compact.includes(compactQ);
  });
}

export function looksLikeCodeOrBarcodeQuery(query) {
  const q = String(query ?? "").trim();
  if (!q) return false;
  const compact = q.replace(/[\s-]/g, "");
  if (/^\d{5,}$/.test(compact)) return true;
  if (/^uktm/i.test(compact)) return true;
  if (/^[a-z0-9]+-\d+/i.test(q.replace(/\s+/g, ""))) return true;
  return !/\s/.test(q) && /[a-z]/i.test(q) && /\d/.test(q) && q.length >= 6;
}

function uniqueMatchingProducts(products, query) {
  const matches = new Map();
  (Array.isArray(products) ? products : []).forEach((product) => {
    if (!product || product.is_active === false) return;
    if (!productMatchesSearchQuery(product, query)) return;
    if (product.id == null) return;
    const key = String(product.id);
    if (!matches.has(key)) matches.set(key, product);
  });
  return Array.from(matches.values());
}

function productMatchesCategorySlug(product, category = "", subcategory = "") {
  const slug = String(subcategory || category || "")
    .trim()
    .toLowerCase();
  if (!slug) return true;
  const productCategory = String(product?.category?.slug ?? "").toLowerCase();
  return productCategory === slug || productCategory.startsWith(`${slug}-`);
}

function productMatchesFilterSlugs(product, filters = []) {
  const needed = uniqueTrimmedStrings(filters).map((slug) =>
    slug.toLowerCase(),
  );
  if (!needed.length) return true;
  const productSlugs = (product?.filters ?? [])
    .map((filter) =>
      String(filter?.slug ?? "")
        .trim()
        .toLowerCase(),
    )
    .filter(Boolean);
  return needed.every((slug) => productSlugs.includes(slug));
}

function paginateProductList(products, page = 1, perPage = PRODUCT_PAGE_SIZE) {
  const size = Math.max(1, Number(perPage) || PRODUCT_PAGE_SIZE);
  const total = products.length;
  const lastPage = Math.max(1, Math.ceil(total / size) || 1);
  const current = Math.min(Math.max(1, Number(page) || 1), lastPage);
  const start = (current - 1) * size;
  return {
    data: products.slice(start, start + size),
    meta: {
      total,
      last_page: lastPage,
      current_page: current,
      per_page: size,
    },
  };
}

const CATALOG_PAGE_DELAY_MS = 500;
const catalogScan = {
  products: new Map(),
  nextPage: 1,
  lastPage: null,
  inflight: null,
  expires: 0,
  generation: 0,
};

function resetCatalogScanIfExpired() {
  if (catalogScan.expires && catalogScan.expires < Date.now()) {
    catalogScan.products = new Map();
    catalogScan.nextPage = 1;
    catalogScan.lastPage = null;
    catalogScan.expires = 0;
  }
}

function catalogIsComplete() {
  return (
    catalogScan.lastPage != null && catalogScan.nextPage > catalogScan.lastPage
  );
}

function getScannedCatalogProducts() {
  return Array.from(catalogScan.products.values());
}

function productExactlyMatchesCodeOrBarcode(product, query) {
  const q = String(query ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]/g, "");
  if (q.length < 5) return false;
  return [
    product?.code,
    product?.sku,
    product?.unit_barcode,
    product?.case_barcode,
    product?.barcode,
  ].some((value) => {
    const compact = String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/[\s-]/g, "");
    return Boolean(compact) && compact === q;
  });
}

export function stopProductCatalogScan() {
  catalogScan.generation += 1;
}

async function fetchNextCatalogPage() {
  if (catalogIsComplete()) return;
  await waitForShopRateLimit();
  if (catalogScan.nextPage > 1) await sleep(CATALOG_PAGE_DELAY_MS);

  const page = catalogScan.nextPage;
  const payload = await fetchShopJson(
    getProductsListUrl({ page, perPage: PRODUCT_BULK_PAGE_SIZE }),
  );
  const data = Array.isArray(payload?.data) ? payload.data : [];
  data.forEach((product) => {
    if (product?.id == null) return;
    catalogScan.products.set(String(product.id), product);
  });

  const metaLast = Number(payload?.meta?.last_page);
  catalogScan.lastPage =
    Number.isFinite(metaLast) && metaLast > 0 ? metaLast : page;
  catalogScan.nextPage = page + 1;

  if (catalogIsComplete()) {
    catalogScan.expires = Date.now() + SHOP_CACHE_TTL_MS;
  }
}

async function ensureNextCatalogPage() {
  if (catalogScan.inflight) {
    await catalogScan.inflight;
    return;
  }
  if (catalogIsComplete()) return;

  const generation = catalogScan.generation;
  catalogScan.inflight = (async () => {
    if (generation !== catalogScan.generation) return;
    await fetchNextCatalogPage();
  })().finally(() => {
    catalogScan.inflight = null;
  });

  await catalogScan.inflight;
}

async function collectCatalogMatches(query, { limit, signal } = {}) {
  resetCatalogScanIfExpired();
  const generation = catalogScan.generation;
  const needed = Math.max(1, Number(limit) || 10);

  const matchesNow = () => uniqueMatchingProducts(getScannedCatalogProducts(), query);

  let matches = matchesNow();
  while (
    matches.length < needed &&
    !catalogIsComplete() &&
    generation === catalogScan.generation &&
    !signal?.aborted
  ) {
    await ensureNextCatalogPage();
    matches = matchesNow();
    if (matches.some((product) => productExactlyMatchesCodeOrBarcode(product, query))) {
      break;
    }
  }

  return matches;
}

export async function searchProducts(query, { perPage = 10, signal } = {}) {
  const q = String(query ?? "").trim();
  if (!q) return [];

  if (!looksLikeCodeOrBarcodeQuery(q)) {
    stopProductCatalogScan();
    const apiResult = await fetchProductsPage({ q, page: 1, perPage }).catch(
      () => ({ data: [] }),
    );
    return uniqueMatchingProducts(apiResult.data, q).slice(0, perPage);
  }

  const matches = await collectCatalogMatches(q, { limit: perPage, signal });
  return matches.slice(0, perPage);
}

export async function fetchProductsForSearch({
  category = "",
  subcategory = "",
  q = "",
  brandId = "",
  brandIds = [],
  filters = [],
  page = 1,
  perPage = PRODUCT_PAGE_SIZE,
} = {}) {
  const query = String(q ?? "").trim();
  const filterSlugs = uniqueTrimmedStrings(filters);
  const resolvedBrandIds = uniqueTrimmedStrings(
    brandIds.length ? brandIds : brandId,
  );
  const primaryBrandId = resolvedBrandIds[0] || "";

  if (!query) {
    return fetchProductsPage({
      category,
      subcategory,
      brandId: primaryBrandId,
      brandIds: resolvedBrandIds,
      filters: filterSlugs,
      page,
      perPage,
    });
  }

  if (primaryBrandId) {
    const brandProducts =
      resolvedBrandIds.length > 1
        ? (
            await Promise.all(
              resolvedBrandIds.map((id) => fetchProductsByBrand(id)),
            )
          ).flat()
        : await fetchProductsByBrand(primaryBrandId);
    const matches = brandProducts.filter(
      (product) =>
        product?.is_active !== false &&
        productMatchesSearchQuery(product, query) &&
        productMatchesCategorySlug(product, category, subcategory) &&
        productMatchesFilterSlugs(product, filterSlugs),
    );
    return paginateProductList(matches, page, perPage);
  }

  if (!looksLikeCodeOrBarcodeQuery(query)) {
    stopProductCatalogScan();
    const apiResult = await fetchProductsPage({
      category,
      subcategory,
      q: query,
      brandId: primaryBrandId,
      brandIds: resolvedBrandIds,
      filters: filterSlugs,
      page,
      perPage,
    });
    const apiData = (apiResult.data || []).filter(
      (product) =>
        product?.is_active !== false &&
        productMatchesFilterSlugs(product, filterSlugs),
    );
    return { ...apiResult, data: apiData };
  }

  const needed = Math.max(1, page) * Math.max(1, perPage);
  const matches = (
    await collectCatalogMatches(query, { limit: needed })
  ).filter(
    (product) =>
      productMatchesCategorySlug(product, category, subcategory) &&
      productMatchesFilterSlugs(product, filterSlugs),
  );
  return paginateProductList(matches, page, perPage);
}

export const getCategoryProductsPath = (categorySlug) =>
  `/products?category=${encodeURIComponent(categorySlug)}`;
